import { Flex, MenuButton, Text } from '@chakra-ui/react';
import {
  Banner,
  Button,
  FormFeedback,
  Menu,
  MenuItem,
  MenuList
} from '@morressier/alembic';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { FormFieldArticleType } from '@publishing-engine/domain/types/article';
import E2E_TEST_SELECTORS from '../../../../e2e/test-selectors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-light-svg-icons';
import { ISubmissionArticleTypesCompatibilityRouteResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';

interface Props {
  config: FormFieldArticleType;
  isLoadingOptions: boolean;
  options: ISubmissionArticleTypesCompatibilityRouteResponse;
}

export const ArticleTypeSelect: React.FC<Props> = ({
  config,
  options,
  isLoadingOptions
}) => {
  const {
    params: { validations, labels }
  } = config;

  const {
    formState: { errors },
    watch,
    setValue
  } = useFormContext();

  const value = watch('newName');
  const error = _.get(errors, 'newName');

  return (
    <Flex
      gap={4}
      bgColor="surfacePrimaryFill"
      flexDirection="column"
      borderRadius="base"
      width={'100%'}
    >
      <Text textStyle="headingGroup">Update {labels.main}</Text>
      <Banner
        variant="attention"
        heading="Data Requirements"
        content="After submission, you can only change the manuscript type if the new type requires the same or fewer data than the current one. Options needing additional data are unavailable at this stage."
      />
      <Flex flexDirection="column" gap={2}>
        <FieldLabel
          isRequired={validations.required}
          label={labels.main}
          subText={labels.subtitle}
        />
        <Menu
          ariaLabel={E2E_TEST_SELECTORS.SUBMISSION_UPLOAD_ARTICLE_TYPE}
          matchWidth
          placement="auto"
        >
          <MenuButton
            variant="outline"
            rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
            justifyContent="space-between"
            backgroundColor={'white'}
            as={Button}
            name={E2E_TEST_SELECTORS.SUBMISSION_UPLOAD_ARTICLE_TYPE}
          >
            <Text textStyle="textBody">
              {isLoadingOptions
                ? 'Fetching Article Types...'
                : options.find(item => item.name === value)?.name ||
                  'Select a Manuscript Type'}
            </Text>
          </MenuButton>
          {!isLoadingOptions && (
            <MenuList maxH="48vh" overflowY="auto" position="relative">
              {options.map(
                ({ isEnabled, name, articleWorkflowId, description }) => (
                  <MenuItem
                    onClick={() => setValue('newName', name)}
                    ariaLabel={name}
                    key={articleWorkflowId}
                    description={description}
                    disabled={!isEnabled}
                  >
                    <Text textStyle="headingLine">{name}</Text>
                  </MenuItem>
                )
              )}
            </MenuList>
          )}
        </Menu>

        {!!error?.message && (
          <FormFeedback variant="error">
            Please select {labels.main} to proceed.
          </FormFeedback>
        )}
      </Flex>
    </Flex>
  );
};
